"use client";

import React, { useState } from "react";
import { ChevronDown, ExternalLink } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/app/_module/components/ui/collapsible";
import { Button } from "@/app/_module/components/ui/button";
import { cn } from "@/app/_module/lib/utils";
import { FactGrid, Panel, focusRing } from "./overview/primitives";

// The IRS online EIN application, step by step, for a solo insurance agent.
const EIN_STEPS: { title: string; description: string; selection?: string; tip?: string }[] = [
  {
    title: "Open the IRS EIN online application",
    description: "Use the button below, or search irs.gov for “Apply for an EIN online”. It's Form SS-4, filled out online.",
  },
  {
    title: "Choose “Apply Online Now”, then “Begin Application”",
    description: "You'll land on the IRS EIN Assistant.",
    tip: "Finish in one sitting. The IRS doesn't let you save and come back.",
  },
  {
    title: "Pick your legal structure",
    description: "When asked what type of legal structure is applying, choose:",
    selection: "Sole Proprietor",
    tip: "Right for most independent agents who run their own business.",
  },
  {
    title: "Pick your reason",
    description: "When asked why you're requesting an EIN, choose:",
    selection: "Started a new business",
    tip: "Use this even if you've been an agent for years but never had an EIN.",
  },
  {
    title: "Confirm you're the sole owner",
    description: "Answer “Yes” when asked if you're the sole member or owner.",
  },
  {
    title: "Enter your personal details",
    description: "Your legal name as it appears on your Social Security card, your SSN and your home address.",
    tip: "If your name changed since your SSN was issued, use the name on the card.",
  },
  {
    title: "Enter your business details",
    description: "Business name, start date and business address.",
    selection: "Your own name, or your DBA such as “John Smith Insurance Services”",
  },
  {
    title: "Choose your business activity",
    description: "Under Finance and Insurance, then Insurance carriers and related activities, choose:",
    selection: "Insurance agents, brokers, and service",
    tip: "This is industry code 524210.",
  },
  {
    title: "Answer the remaining questions",
    description: "Employees and expected tax filings. Most solo agents answer “No” to hiring employees and to paying $4,000 or more in wages.",
  },
  {
    title: "Review and submit",
    description: "Your EIN appears on screen right away.",
    tip: "Save or print that confirmation page. It's your CP 575, which the next step asks for.",
  },
];

export default function EINGuide() {
  const [isOpen, setIsOpen] = useState(true);

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <Panel className="overflow-hidden">
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className={cn(
              "flex min-h-14 w-full items-center justify-between gap-3 px-4 py-3 text-left transition-colors hover:bg-neutral-50 sm:px-5",
              focusRing,
              "focus-visible:-outline-offset-2",
            )}
          >
            <span>
              <span className="block text-sm font-semibold text-neutral-900">How to get an EIN</span>
              <span className="block text-sm text-neutral-500">Free from the IRS, online, in about 10-15 minutes</span>
            </span>
            <ChevronDown aria-hidden className={cn("size-4 shrink-0 text-neutral-500 transition-transform", isOpen && "rotate-180")} />
          </button>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="space-y-6 border-t border-neutral-100 px-4 py-5 sm:px-5">
            <FactGrid
              facts={[
                { label: "Cost", value: "Free" },
                { label: "When", value: "Mon-Fri, 7 AM-10 PM ET" },
                { label: "Your EIN", value: "Issued on screen" },
              ]}
            />

            <div>
              <p className="text-sm font-medium text-neutral-900">Have these ready</p>
              <ul className="mt-2 grid list-disc gap-x-6 gap-y-1 pl-5 text-sm leading-relaxed text-neutral-700 marker:text-neutral-400 sm:grid-cols-2">
                <li>Your Social Security Number</li>
                <li>Your legal name as on your SSN card</li>
                <li>Your business start date</li>
                <li>Your business address (your home address works)</li>
              </ul>
            </div>

            <ol className="space-y-5">
              {EIN_STEPS.map((step, i) => (
                <li key={step.title} className="flex gap-3.5">
                  <span
                    aria-hidden
                    className="flex size-6 shrink-0 items-center justify-center rounded-full bg-neutral-100 text-xs font-semibold tabular-nums text-neutral-700"
                  >
                    {i + 1}
                  </span>
                  <div className="min-w-0 flex-1 space-y-1">
                    <p className="text-sm font-medium text-neutral-900">{step.title}</p>
                    <p className="text-pretty text-sm leading-relaxed text-neutral-600">{step.description}</p>
                    {step.selection && (
                      <p className="inline-block rounded-md bg-neutral-50 px-2.5 py-1 text-sm font-medium text-neutral-900 ring-1 ring-inset ring-neutral-200">
                        {step.selection}
                      </p>
                    )}
                    {step.tip && <p className="text-pretty text-xs leading-relaxed text-neutral-500">{step.tip}</p>}
                  </div>
                </li>
              ))}
            </ol>

            <div className="space-y-2">
              <Button asChild size="lg" className={cn("w-full px-6 sm:w-auto", focusRing)}>
                <a
                  href="https://www.irs.gov/businesses/small-businesses-self-employed/apply-for-an-employer-identification-number-ein-online"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Apply on IRS.gov
                  <ExternalLink />
                </a>
              </Button>
              <p className="text-xs text-neutral-500">Opens the official IRS site in a new tab.</p>
            </div>
          </div>
        </CollapsibleContent>
      </Panel>
    </Collapsible>
  );
}
